import { useCallback, useEffect, useRef, useState } from 'react';
import { workspaceApi } from './workspaceApi';
import { proshopError } from '../proshopApi';
import { restrictExpiredAccess, validateWorkspace } from './workspaceData';

export const useWorkspace = (id, key) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const version = useRef(0);
  const inFlight = useRef(false);
  const refresh = useCallback(async (quiet = false) => {
    if (quiet === true && inFlight.current) return;
    const current = ++version.current; setLoading(true); setError('');
    inFlight.current = true;
    if (quiet !== true && key) setData(d => d ? { ...d, capabilities: { ...d.capabilities, can_view_financials: false } } : d);
    try {
      const result = validateWorkspace(await workspaceApi.load(id, key), id);
      if (current === version.current) setData(restrictExpiredAccess(result));
    } catch (e) { if (current === version.current) { setError(proshopError(e)); setData(null); } }
    finally { if (current === version.current) { setLoading(false); inFlight.current = false; } }
  }, [id, key]);
  useEffect(() => {
    setData(null); refresh();
    const focus = () => refresh();
    const visibility = () => {
      if (document.visibilityState === 'visible') refresh();
      else if (key) { version.current++; inFlight.current = false; setData(d => d ? { ...d, capabilities: { ...d.capabilities, can_view_financials: false } } : d); }
    };
    // A visible dashboard rechecks owner revocations without overlapping requests.
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(true); }, 10000);
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visibility);
    return () => { version.current++; inFlight.current = false; clearInterval(timer); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visibility); };
  }, [refresh, key]);
  useEffect(() => {
    const deadlines = [data?.capabilities.edit_expires_at, data?.capabilities.finance_expires_at].filter(Boolean).map(v => new Date(v).getTime()).filter(v => v > Date.now());
    if (!deadlines.length) return undefined;
    // UI expiry only; the backend independently checks every privileged request.
    const timer = setTimeout(() => { setData(restrictExpiredAccess); refresh(true); }, Math.min(...deadlines) - Date.now() + 5);
    return () => clearTimeout(timer);
  }, [data?.capabilities.edit_expires_at, data?.capabilities.finance_expires_at, refresh]);
  return { data, error, loading, refresh };
};